import type {
  IPracticeMigration,
  IGetRecordResponse,
  ITranslationMap,
  IDestinationEntity,
  IDestinationEntityJobRunOptions,
  FailedDestinationEntityRecord,
  IDestinationEntityRecord,
  IHasSourceIdentifier,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Firestore,
  ISODateType,
  Timezone,
  WithPrimaryKey,
  type WithRef,
  getError,
  resolveSequentially,
  snapshotCombineLatest,
  sortTimestamp,
  toISODate,
  toMomentTz,
  toNamedDocument,
  toTimestamp,
} from '@principle-theorem/shared';
import { isNull, isEmpty, groupBy } from 'lodash';
import { ADAItemSourceEntity } from '../../../exact/source/entities/ada-item';
import {
  ExactTreatment,
  PatientTreatmentSourceEntity,
} from '../../../exact/source/entities/patient-treatments';
import { isClinicalNoteCode } from '../../../mappings/item-code-mapping-helpers';
import {
  BasePatientClinicalNoteDestinationEntity,
  IClinicalNoteJobData,
  IClinicalNoteMigrationData,
} from '../../../destination/entities/patient-clinical-notes';
import { ItemCodeNoteType } from '../../../mappings/item-codes-to-notes-xlsx';
import { ExactItemCodeToNoteMappingHandler } from '../../../exact/destination/mappings/item-code-to-notes';
import { resolveExactStaffer } from '../../../exact/destination/mappings/staff';
import {
  ExactBPEScore,
  PATIENT_BPE_RESOURCE_TYPE,
  PatientBPESourceEntity,
} from '../../../exact/source/entities/patient-bpe-scores';
import {
  Brand,
  ClinicalNote,
  stafferToNamedDoc,
  toINamedDocuments,
} from '@principle-theorem/principle-core';
import type {
  IClinicalNote,
  IPatient,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import { Observable, from, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { PracticeMigration } from '../../../practice-migrations';
import { TranslationMapHandler } from '../../../translation-map';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import { Timestamp } from '@google-cloud/firestore';
import {
  initVersionedSchema,
  ITextNodeSchema,
  toTextContent,
} from '@principle-theorem/editor';
import {
  PatientSourceEntity,
  PracticeWorksPatient,
} from '../../source/entities/patients';
import { PracticeWorksItemCodeMappingHandler } from '../mappings/item-codes';
import { PracticeWorksStafferMappingHandler } from '../mappings/staff';
import { PatientDestinationEntity } from './patient';

export interface IPracticeWorksClinicalNoteJobData
  extends IClinicalNoteJobData<PracticeWorksPatient> {
  itemCodesToNotes: WithPrimaryKey<ITranslationMap<object, ItemCodeNoteType>>[];
}

export class PatientClinicalNoteDestinationEntity extends BasePatientClinicalNoteDestinationEntity<PracticeWorksPatient> {
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    treatments: new PatientTreatmentSourceEntity(),
    bpeScores: new PatientBPESourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  customMappings = {
    staff: new PracticeWorksStafferMappingHandler(),
    itemCodes: new PracticeWorksItemCodeMappingHandler(),
    itemCodesToNotes: new ExactItemCodeToNoteMappingHandler(),
  };

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPracticeWorksClinicalNoteJobData
  ): Promise<
    | IClinicalNoteMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = this.sourceEntities.patients.getSourceRecordId(
        data.sourcePatient.data
      );
      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId,
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(
          data.sourcePatient.record,
          `Couldn't resolve patient`
        );
      }

      const patientUid = this.sourceEntities.patients.getSourceRecordId(
        data.sourcePatient.data
      );
      const clinicalNoteTreatments = await this.getClinicalNoteTreatments(
        migration,
        patientUid,
        data
      );
      const bpeScores = await this._getBpeScores(migration, patientUid);
      const clinicalNotes = await this._buildClinicalNotesData(
        clinicalNoteTreatments,
        bpeScores,
        migration.configuration.timezone,
        data,
        translationMap
      );

      return {
        sourcePatientId,
        patientRef,
        clinicalNotes,
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPracticeWorksClinicalNoteJobData[]> {
    const brand$ = PracticeMigration.brand$(migration).pipe(
      map(toNamedDocument)
    );
    const practitioners$ = from(brand$).pipe(
      switchMap((brand) => Firestore.getDocs(Brand.stafferCol(brand))),
      toINamedDocuments()
    );
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const itemCodesToNotes$ =
      this.customMappings.itemCodesToNotes.getRecords(translationMap);
    const sourceItemCodes$ =
      this.customMappings.itemCodes.getRecords(translationMap);

    return snapshotCombineLatest([
      staff$,
      practitioners$,
      sourceItemCodes$,
      itemCodesToNotes$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          sourcePatients,
          [staff, practitioners, sourceItemCodes, itemCodesToNotes],
        ]) =>
          sourcePatients.map((sourcePatient) => ({
            staff,
            practitioners,
            sourceItemCodes,
            itemCodesToNotes,
            sourcePatient,
            destinationEntity,
          }))
      )
    );
  }

  async getClinicalNoteTreatments(
    migration: WithRef<IPracticeMigration>,
    patientUid: string,
    data: IPracticeWorksClinicalNoteJobData
  ): Promise<IGetRecordResponse<ExactTreatment>[]> {
    const timezone = migration.configuration.timezone;
    const clinicalNoteTreatments =
      await this.sourceEntities.treatments.filterRecords(
        migration,
        'patientId',
        patientUid,
        (record) =>
          !isNull(record.data.treatment_notes) &&
          record.data.treatment_notes !== record.data.service_description
      );

    const sourceEntity = new ADAItemSourceEntity();
    return clinicalNoteTreatments
      .filter((record) => {
        const sourceId = sourceEntity.getSourceRecordId({
          itemCode: record.data.service_code,
        });
        return (
          isClinicalNoteCode(
            record.data.service_code,
            sourceId,
            data.sourceItemCodes,
            data.itemCodesToNotes
          ) ||
          (isEmpty(record.data.service_code) &&
            !isEmpty(record.data.treatment_notes))
        );
      })
      .sort((treatmentA, treatmentB) =>
        sortTimestamp(
          this.getNoteTimestamp(treatmentA, timezone),
          this.getNoteTimestamp(treatmentB, timezone)
        )
      );
  }

  getNoteDate(
    sourceNote: IGetRecordResponse<ExactTreatment>
  ): ISODateType | undefined {
    return (
      sourceNote.record.translations.completedDate ??
      sourceNote.record.translations.plannedDate
    );
  }

  getNoteTimestamp(
    sourceNote: IGetRecordResponse<ExactTreatment>,
    timezone: Timezone
  ): Timestamp {
    const noteDate = this.getNoteDate(sourceNote);
    return noteDate
      ? toTimestamp(toMomentTz(noteDate, timezone))
      : toTimestamp();
  }

  private async _getBpeScores(
    migration: WithRef<IPracticeMigration>,
    patientUid: string
  ): Promise<IGetRecordResponse<ExactBPEScore>[]> {
    const timezone = migration.configuration.timezone;
    const patientBpeScores = await this.sourceEntities.bpeScores.filterRecords(
      migration,
      'patientId',
      patientUid
    );
    if (!patientBpeScores.length) {
      return [];
    }

    return patientBpeScores.sort((scoreA, scoreB) =>
      sortTimestamp(
        toTimestamp(toMomentTz(scoreA.record.translations.date, timezone)),
        toTimestamp(toMomentTz(scoreB.record.translations.date, timezone))
      )
    );
  }

  private _treatmentsToNoteContent(
    notes: IGetRecordResponse<ExactTreatment>[]
  ): ITextNodeSchema[] {
    return notes
      .reduce((results: IGetRecordResponse<ExactTreatment>[], note) => {
        const existing = results.find(
          (result) => result.data.treatment_notes === note.data.treatment_notes
        );
        if (!existing) {
          results.push(note);
        }
        return results;
      }, [])
      .map((note) => [
        toTextContent(
          `${note.data.service_code} ${note.data.tooth ?? ''} ${
            note.data.tooth_surfaces.length
              ? note.data.tooth_surfaces.join(', ')
              : ''
          } `
        ),
        toTextContent(`${note.data.treatment_notes ?? ''}\n\n`),
      ])
      .flat();
  }

  private async _buildClinicalNotesData(
    clinicalNoteTreatments: IGetRecordResponse<ExactTreatment>[],
    bpeScores: IGetRecordResponse<ExactBPEScore>[],
    timezone: Timezone,
    data: IPracticeWorksClinicalNoteJobData,
    translationMap: TranslationMapHandler
  ): Promise<(IClinicalNote & IHasSourceIdentifier)[]> {
    const clinicalNotes: (IClinicalNote & IHasSourceIdentifier)[] = [];

    const userGroupedNoteTreatments = groupBy(
      clinicalNoteTreatments,
      (record) => record.data.provider_code ?? '0'
    );

    await resolveSequentially(
      Object.entries(userGroupedNoteTreatments),
      async ([providerId, userNotes]) => {
        const staffer = await resolveExactStaffer(
          providerId,
          translationMap,
          data.staff
        );
        if (!staffer) {
          throw new Error(`Failed to resolve practitioner code: ${providerId}`);
        }

        const dateGroupedNotes = groupBy(userNotes, (note) =>
          this.getNoteDate(note)
        );
        Object.values(dateGroupedNotes).map((notes) => {
          const content = this._treatmentsToNoteContent(notes);
          const noteDate = this.getNoteDate(notes[0]);
          const noteTimestamp = this.getNoteTimestamp(notes[0], timezone);
          clinicalNotes.push({
            sourceIdentifier: `${notes[0].data.patient_id}-${providerId}-${noteDate}`,
            ...ClinicalNote.init({
              owner: stafferToNamedDoc(staffer),
              content: initVersionedSchema(content),
              recordDate: noteDate ?? toISODate(toTimestamp()),
              createdAt: noteTimestamp,
              updatedAt: noteTimestamp,
              immutable: true,
            }),
          });
        });
      }
    );

    const userGroupedBpeScores = groupBy(
      bpeScores,
      (record) => record.data.user_code ?? '0'
    );

    await resolveSequentially(
      Object.entries(userGroupedBpeScores),
      async ([providerId, userScores]) => {
        const staffer = await resolveExactStaffer(
          providerId,
          translationMap,
          data.staff
        );
        if (!staffer) {
          throw new Error(
            `Failed to resolve BPE practitioner code: ${providerId}`
          );
        }

        userScores.map((score) => {
          const scoreParts = score.data.scores.split('\r\n');
          const formattedScores = `${scoreParts
            .slice(0, 3)
            .join(' ')}\n ${scoreParts.slice(3).join(' ')}\n\n`;
          const content = [
            toTextContent(formattedScores),
            toTextContent(`${score.data.notes || ' '}\n\n`),
          ];

          const scoreTimestamp = toTimestamp(
            toMomentTz(score.record.translations.date, timezone)
          );
          const noteDate = toISODate(scoreTimestamp);

          clinicalNotes.push({
            sourceIdentifier: `${score.data.patient_id}-${providerId}-${noteDate}-${PATIENT_BPE_RESOURCE_TYPE}`,
            ...ClinicalNote.init({
              owner: stafferToNamedDoc(staffer),
              content: initVersionedSchema(content),
              recordDate: noteDate,
              createdAt: scoreTimestamp,
              updatedAt: scoreTimestamp,
              immutable: true,
            }),
          });
        });
      }
    );

    return clinicalNotes;
  }
}
